<script lang="ts">
  // What the keyboard is waiting for, and what it last said: the count and
  // keys typed so far with the keys that may follow, and the short notice
  // for a command that had nothing to act on. One instance, in App; small,
  // in a corner, and never in the way of the page.
  import { WAITING } from '../commands/command';
  import { NOUN, phrase } from '../commands/words';
  import { keyLabel } from '../keys/chord';
  import { keyboard } from '../keys/keyboard.svelte';

  const pending = $derived(keyboard.pending);
  const notice = $derived(keyboard.notice);
  /** The keys that may follow that the page has something for; deprecated aliases are not offered. */
  const offered = $derived((pending?.next ?? []).filter((n) => n.alias !== 'deprecated' && (!n.command || keyboard.offers(n.command))));
  const next = $derived(
    offered.map((n) => ({
      key: keyLabel(n.key),
      does: n.object && n.command && 'object' in n.command && n.command.object ? NOUN[n.command.object] : n.command ? phrase(n.command) : '…',
    })),
  );
  /** An object letter's capital means something here: a motion that stops only at what waits. */
  const capitals = $derived(
    offered.some((n) => {
      const c = n.object ? n.command : null;
      const only = c?.kind === 'move' ? WAITING[c.object] : null;
      return !!c && c.kind === 'move' && !!only && keyboard.offers({ ...c, only });
    }),
  );
</script>

{#if pending}
  <div class="keyhint" aria-hidden="true">
    <div class="typed">
      {#if pending.count !== null}<kbd>{pending.count}</kbd>{/if}{#each pending.keys as k}<kbd>{keyLabel(k)}</kbd>{/each}<span class="more">…</span>
    </div>
    {#if next.length}
      <ul class:phrases={!offered.some((n) => n.object)}>
        {#each next as n (n.key)}
          <li><kbd>{n.key}</kbd> <span>{n.does}</span></li>
        {/each}
      </ul>
      {#if capitals}<p>A capital letter: only what still waits for you.</p>{/if}
    {/if}
  </div>
{:else if notice}
  <div class="keyhint said">{notice.text}</div>
{/if}
<!-- A notice is read out once, whatever is drawn. -->
<div class="sr-only" role="status">{#key notice?.id}{notice?.text ?? ''}{/key}</div>

<style>
  .keyhint {
    position: fixed;
    right: 16px;
    bottom: 16px;
    z-index: 45;
    max-width: min(340px, calc(100vw - 32px));
    background: var(--surface);
    color: var(--fg);
    border: 1px solid var(--line);
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 12px;
    line-height: 1.5;
    box-shadow: 0 10px 30px #0002;
    pointer-events: none;
  }
  .said {
    color: var(--muted);
  }
  .typed {
    display: flex;
    align-items: center;
    gap: 3px;
  }
  .typed kbd {
    color: var(--fg);
    background: var(--soft);
    border-color: var(--accent);
  }
  .more {
    color: var(--muted);
    margin-left: 2px;
  }
  ul {
    list-style: none;
    margin: 6px 0 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2px 12px;
  }
  /* Phrases need the width; object names sit two abreast. */
  ul.phrases {
    grid-template-columns: minmax(0, 1fr);
  }
  li {
    display: flex;
    gap: 6px;
    align-items: baseline;
    min-width: 0;
  }
  li span {
    color: var(--muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  li kbd {
    color: var(--fg);
    flex: none;
  }
  p {
    margin: 6px 0 0;
    color: var(--muted);
  }
</style>
